<template>
  <div class="navigation-bar">
    <button class="navigation-bar__menu" @click="handleToggle()">
      <BaseIcon name="menu" />
    </button>

    <!--
      Only ever one banner at a time — stacking them would push the whole app down and train people
      to ignore both — so the order is a ranking. A failed payment comes first: it means money is
      owed now, and a tenant can be both in trial (Aloha's dates) and past due (Stripe's status) when
      a trial was ended early. Then a subscription that ended, the trial reminder, and last the card
      warning, which is about later.
    -->
    <BaseInfoBanner
      v-if="showPaymentIssueBanner"
      class="navigation-bar__trial"
      tone="destructive"
      preset="soft"
      :title="$t('navigation-bar.payment-failed')"
    >
      <template #icon>
        <BaseIcon name="alert-triangle" />
      </template>
      <template #action>
        <a v-if="latestInvoiceUrl" :href="latestInvoiceUrl" target="_blank" rel="noopener noreferrer">
          {{ $t('navigation-bar.payment-failed-action') }}
        </a>
        <button v-else type="button" @click="goToBilling">
          {{ $t('navigation-bar.payment-failed-action') }}
        </button>
      </template>
    </BaseInfoBanner>

    <BaseInfoBanner
      v-else-if="showSubscriptionEndedBanner"
      class="navigation-bar__trial"
      tone="destructive"
      preset="soft"
      :title="$t('navigation-bar.subscription-ended')"
    >
      <template #icon>
        <BaseIcon name="alert-triangle" />
      </template>
      <template #action>
        <button type="button" @click="onBookCall">
          {{ $t('navigation-bar.contact-us') }}
        </button>
      </template>
    </BaseInfoBanner>

    <BaseInfoBanner
      v-else-if="showTrialBanner"
      class="navigation-bar__trial"
      :tone="trialBannerTone"
      preset="soft"
      :title="trialBannerTitle"
    >
      <template #icon>
        <BaseIcon name="alert-triangle" />
      </template>
      <template #action>
        <button type="button" @click="onBookCall">
          {{ $t('navigation-bar.trial-book-call') }}
        </button>
      </template>
    </BaseInfoBanner>

    <BaseInfoBanner
      v-else-if="showCardExpiryBanner"
      class="navigation-bar__trial"
      :tone="cardBannerTone"
      preset="soft"
      :title="cardBannerTitle"
    >
      <template #icon>
        <BaseIcon name="credit-card" />
      </template>
      <template #action>
        <button type="button" @click="goToBilling">
          {{ $t('navigation-bar.card-expiring-action') }}
        </button>
      </template>
    </BaseInfoBanner>

    <div class="navigation-bar__options">
      <div v-if="scheduledTasks.length > 0" class="navigation-bar__section">
        <BaseScheduledTasks :items="scheduledTasks" />
      </div>
      <div class="navigation-bar__section">
        <Beacon />
      </div>
      <div class="navigation-bar__section">
        <NavigationBarNotifications />
      </div>

      <NavigationBarProfile
        v-if="currentUser"
        :color="currentUser.avatarBackgroundColor"
        :name="currentUser.firstName + ' ' + currentUser.lastName"
        :email="currentUser.email"
      />
    </div>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import BaseInfoBanner from '@/components/base/BaseInfoBanner';
import Beacon from '@/components/Beacon';
import NavigationBarNotifications from '@/components/notifications-center/NavigationBarNotifications.vue';
import NavigationBarProfile from '@/components/layout/NavigationBarProfile';
import { mapActions, mapGetters } from 'vuex';
import BaseScheduledTasks from '@/components/base/BaseScheduledTasks.vue';
import { BI_CALENDAR_URL } from '@/util/tier-mapping';
import { DISPLAY_STEPS, getDisplayStepIndex } from '@/models/extraction-steps';
import { isManager } from '@/models/role';
import { paths } from '@/util/paths';

export default {
  name: 'NavigationBar',
  components: {
    BaseScheduledTasks,
    BaseIcon,
    BaseInfoBanner,
    Beacon,
    NavigationBarNotifications,
    NavigationBarProfile,
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('tenant', ['trialOn', 'daysBeforeTrialEnds']),
    ...mapGetters('chromeExtension', { chromeExtensionTasks: 'tasks' }),
    ...mapGetters('formFill', ['fillTasks']),
    ...mapGetters('extractionJob', ['extractionTasks']),
    ...mapGetters('billing', [
      'hasPaymentIssue',
      'subscriptionEnded',
      'cardWarningTone',
      'cardExpiry',
      'latestInvoiceUrl',
    ]),
    scheduledTasks() {
      return [...this.chromeExtensionScheduledTasks, ...this.formFillScheduledTasks, ...this.extractionScheduledTasks];
    },
    chromeExtensionScheduledTasks() {
      return this.chromeExtensionTasks
        .filter((task) => !!task.opportunity)
        .map((task) => {
          const progressPercent =
            task.steps.length > 0 && task.stepCompletedIndex >= 0
              ? Math.max(0, Math.min(100, (100 * task.stepCompletedIndex + 1) / task.steps.length))
              : 0;
          return {
            title: task.opportunity.name,
            subtitle: task.opportunity.tender.procuringEntity.name,
            status: task.stepCompletedIndex < 0 ? 'pending' : progressPercent === 100 ? 'done' : 'inProgress',
            percent: progressPercent,
          };
        });
    },
    // One running-task entry per opportunity being filled; the percentage is
    // documents filled vs documents triggered to be filled.
    formFillScheduledTasks() {
      return this.fillTasks.map((task) => {
        const percent = task.total > 0 ? Math.max(0, Math.min(100, (100 * task.completed) / task.total)) : 0;
        return {
          title: task.opportunityName || this.$t('tasks-queue.form-fill'),
          subtitle: this.$tc('tasks-queue.form-fill-progress', task.completed, {
            completed: task.completed,
            total: task.total,
          }),
          status: percent === 100 ? 'done' : 'inProgress',
          percent,
        };
      });
    },
    // One entry per opportunity with a running extraction job (the sessions
    // live in the extractionJob module and survive navigation).
    extractionScheduledTasks() {
      return this.extractionTasks.map((task) => {
        const stepIndex = getDisplayStepIndex(task.step);
        const fallbackTitle =
          task.jobType === 'requirementsExtraction'
            ? this.$t('tasks-queue.requirements-extraction')
            : this.$t('tasks-queue.fields-extraction');
        return {
          title: task.opportunityName || fallbackTitle,
          subtitle: this.$t(`documents-summary.extraction-steps.${DISPLAY_STEPS[stepIndex].displayKey}`),
          status: 'inProgress',
          percent: (100 * stepIndex) / DISPLAY_STEPS.length,
        };
      });
    },
    // Whole days remaining from tenant DHM ([d, h, m]); null when ended or unavailable.
    daysUntilTrialEnds() {
      if (!this.daysBeforeTrialEnds) return null;
      return Number(this.daysBeforeTrialEnds[0]) || 0;
    },
    // Within 7 days while trial is on.
    showTrialBanner() {
      const days = this.daysUntilTrialEnds;
      if (days === null) return false;
      return this.trialOn && days <= 7;
    },
    // Warning for 3–7 days left; red below 3 days.
    trialBannerTone() {
      const days = this.daysUntilTrialEnds;
      if (days === null || days < 3) return 'destructive';
      return 'warning';
    },
    trialBannerTitle() {
      const days = this.daysUntilTrialEnds;
      if (days === null) return '';
      if (days === 0) return this.$t('navigation-bar.trial-ends-today');
      return this.$tc('navigation-bar.trial-ending', days, { count: days });
    },

    /**
     * Billing banners are for the people who can act on them.
     *
     * A regular user cannot update a card or pay an invoice, and showing them the company's payment
     * troubles is both useless and a small privacy leak. Manager and up only — the same audience the
     * billing page itself is gated to.
     */
    canSeeBillingBanners() {
      return isManager(this.currentUser && this.currentUser.role);
    },

    showPaymentIssueBanner() {
      return this.canSeeBillingBanners && this.hasPaymentIssue;
    },

    showSubscriptionEndedBanner() {
      return this.canSeeBillingBanners && this.subscriptionEnded;
    },

    showCardExpiryBanner() {
      return this.canSeeBillingBanners && !!this.cardWarningTone;
    },

    // `expired` and `critical` (within 14 days) are red; the 60-day heads-up is amber.
    cardBannerTone() {
      return this.cardWarningTone === 'warning' ? 'warning' : 'destructive';
    },

    cardBannerTitle() {
      if (this.cardWarningTone === 'expired') {
        return this.$t('navigation-bar.card-expired', { expiry: this.cardExpiry });
      }
      return this.$t('navigation-bar.card-expiring', { expiry: this.cardExpiry });
    },
  },

  methods: {
    ...mapActions('billing', ['getMyBilling']),

    /** Re-read billing when the user comes back to the tab while a billing banner is showing. */
    refetchBillingOnReturn() {
      if (document.visibilityState !== 'visible' || this.billingRefetch) return;
      if (!this.showPaymentIssueBanner && !this.showSubscriptionEndedBanner && !this.showCardExpiryBanner) return;
      // A tab switch fires both visibilitychange and focus: one request is enough.
      this.billingRefetch = this.getMyBilling().finally(() => {
        this.billingRefetch = null;
      });
    },

    goToBilling() {
      this.$router.push(paths.adminBilling);
    },

    handleToggle() {
      this.$emit('handleToggle');
    },
    onBookCall() {
      if (BI_CALENDAR_URL && BI_CALENDAR_URL !== '#') {
        window.open(BI_CALENDAR_URL, '_blank', 'noopener,noreferrer');
      }
    },
  },

  /** Once per session, then on return to the tab (e.g. after paying in Stripe). Managers and up only. */
  created() {
    if (!this.canSeeBillingBanners) return;
    this.getMyBilling();
    document.addEventListener('visibilitychange', this.refetchBillingOnReturn);
    window.addEventListener('focus', this.refetchBillingOnReturn);
  },

  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.refetchBillingOnReturn);
    window.removeEventListener('focus', this.refetchBillingOnReturn);
  },
};
</script>

<style lang="scss">
.navigation-bar {
  z-index: $nav-bar-index;
  background: $color-white;
  border-bottom: 1px solid $color-border;
  display: flex;
  align-items: center;
  height: $menu-top-bar-height;
  position: -webkit-sticky;
  position: sticky !important;
  top: 0px;

  &__menu {
    display: none;
  }

  &__trial.base-info-banner {
    margin-left: 16px;
    flex-shrink: 1;
    min-width: 0;
    padding: 4px 8px;
    gap: 8px;

    .base-info-banner__action a,
    .base-info-banner__action button {
      padding: 2px 4px;
      font-size: 11px;
      font-weight: 600;
      white-space: nowrap;
    }
  }

  &__options {
    align-items: center;
    display: flex;
    justify-content: flex-end;
    margin-left: auto;
  }

  &__section {
    padding: 0 16px;
    position: relative;

    &:after {
      background: $color-border;
      content: '';
      display: block;
      height: 24px;
      position: absolute;
      right: 0;
      top: calc(50% - 12px);
      width: 1px;
    }
  }

  @media screen and (max-width: 1080px) {
    &__menu {
      display: flex;
      align-self: center;
      padding-left: 32px;

      .feather {
        color: $color-neutral;
        transition: color 50ms ease-in-out;

        &:hover {
          color: $color-accent;
          transition: color 250ms ease-in-out;
        }
      }
    }
  }

  // Phones: out of the way while the page scrolls down (see top-bar-follower).
  @include screen-below('tablet') {
    transition: transform $top-bar-slide;

    .base-layout--top-bar-hidden & {
      transform: translateY(-100%);
    }

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
}
</style>
